import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Badge, Spinner } from "react-bootstrap";
import {
  IconRocket,
  IconCheck,
  IconCreditCard,
  IconAlertTriangle,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { setCookie } from "nookies";
import jwt_decode from "jwt-decode";
import PlanServices from "../../../services/PlanServices";
import CustomerBillingProfileServices from "../../../services/CustomerBillingProfileServices";
import ModalComponent from "../../../jsx/components/Modal";
import NewTermsOfUse from "../../../jsx/components/ModalData/NewTermsOfUse";

const SubscribePlanModal = ({ show, onHide, plan, currentPlan, onConfirm }) => {
  const navigate = useNavigate();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingBillingProfile, setCheckingBillingProfile] = useState(true);
  const [hasBillingProfile, setHasBillingProfile] = useState(false);
  const [subscribeResult, setSubscribeResult] = useState(null);
  const [subscribeError, setSubscribeError] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    if (show && plan) {
      checkBillingProfile();
      setAcceptedTerms(false);
      setSubscribeResult(null);
      setSubscribeError(null);
    }
  }, [show, plan]);

  const checkBillingProfile = async () => {
    setCheckingBillingProfile(true);
    try {
      const response = await CustomerBillingProfileServices.listCustomerBillingProfiles({
        page_size: 1,
        is_active: true,
      });
      const hasProfile = response.data?.results?.length > 0 || response.data?.count > 0;
      setHasBillingProfile(hasProfile);
    } catch (error) {
      console.error("Erro ao verificar perfil de faturamento:", error);
      setHasBillingProfile(false);
    } finally {
      setCheckingBillingProfile(false);
    }
  };

  if (!plan) return null;

  const isTrialExpired = !!currentPlan?.trial_expired;
  const isTrialActive = !!currentPlan?.is_trial && !isTrialExpired;

  const handleConfirm = async () => {
    if (!acceptedTerms) return;
    setLoading(true);
    setSubscribeError(null);
    try {
      const response = await PlanServices.subscribePlan(plan.id);
      if (response.data?.success) {
        const accessToken = response.data?.auth?.access || response.data?.tokens?.access;
        const refreshToken = response.data?.auth?.refresh || response.data?.tokens?.refresh;
        if (accessToken) {
          setCookie(undefined, "token", accessToken, {
            maxAge: 60 * 60 * 24 * 14,
            path: "/",
            sameSite: "Lax",
          });
          const decoded = jwt_decode(accessToken);
          if (decoded.plan_id) {
            sessionStorage.setItem("plan_id", decoded.plan_id);
          }
        }
        if (refreshToken) {
          setCookie(undefined, "refresh_token", refreshToken, {
            maxAge: 60 * 60 * 24 * 14,
            path: "/",
            sameSite: "Lax",
          });
        }
        setSubscribeResult(response.data);
      }
    } catch (error) {
      const errorInfo = PlanServices.processPlanError(error);
      setSubscribeError(errorInfo.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToBilling = () => {
    onHide();
    navigate("/billing");
  };

  const handleClose = () => {
    if (subscribeResult) {
      if (onConfirm) onConfirm(plan, subscribeResult);
      window.location.reload();
      return;
    }
    setSubscribeResult(null);
    setSubscribeError(null);
    onHide();
  };

  if (checkingBillingProfile) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="mb-0">Verificando perfil de faturamento...</p>
        </Modal.Body>
      </Modal>
    );
  }

  if (subscribeResult) {
    const formatDate = (dateString) => {
      if (!dateString) return "—";
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR");
    };

    return (
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton className="border-0 bg-success">
          <Modal.Title className="d-flex align-items-center text-white">
            <IconCheck size={28} className="me-2" />
            Plano Contratado com Sucesso!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success" className="mb-4">
            <div className="d-flex align-items-center">
              <IconRocket size={24} className="me-3 flex-shrink-0" />
              <div>
                <strong>{subscribeResult.message || "Pagamento confirmado"}</strong>
                <p className="mb-0 mt-1">
                  Seu plano <strong>{subscribeResult.plan?.name || plan.name}</strong> está ativo.
                </p>
              </div>
            </div>
          </Alert>

          <div className="bg-light rounded p-4 mb-4">
            <h6 className="mb-3 d-flex align-items-center">
              <IconCreditCard size={18} className="me-2" />
              Resumo da Cobrança
            </h6>
            <div className="row g-3">
              <div className="col-6">
                <small className="text-muted d-block">Plano contratado</small>
                <strong className="text-primary">
                  {subscribeResult.plan?.name || plan.name}
                </strong>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">Valor cobrado hoje</small>
                <strong className="fs-5 text-primary">
                  {PlanServices.formatCurrency(
                    subscribeResult.payment?.value || subscribeResult.plan?.value || plan.monthly_value,
                  )}
                </strong>
              </div>
              {subscribeResult.subscription?.next_due_date && (
                <div className="col-12 border-top pt-3 mt-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">Próximo vencimento:</span>
                    <strong>{formatDate(subscribeResult.subscription.next_due_date)}</strong>
                  </div>
                </div>
              )}
              <div className="col-12">
                <Badge bg="success" className="px-3 py-2">
                  <IconCheck size={14} className="me-1" />
                  Pagamento Confirmado
                </Badge>
              </div>
            </div>
          </div>

          <Alert variant="info" className="mb-0">
            <IconInfoCircle size={18} className="me-2" />
            Sua assinatura foi ativada. Aproveite todos os recursos do plano!
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="success" onClick={handleClose} size="lg">
            <IconCheck size={18} className="me-2" />
            Entendido
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  if (!hasBillingProfile) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="d-flex align-items-center">
            <IconCreditCard size={28} className="text-warning me-2" />
            Perfil de Faturamento Necessário
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-4">
            <div className="d-flex align-items-start">
              <IconAlertTriangle size={24} className="me-3 flex-shrink-0 mt-1" />
              <div>
                <strong>Atenção!</strong>
                <p className="mb-0 mt-2">
                  Para contratar seu plano, cadastre antes um perfil de faturamento com os dados de pagamento.
                </p>
              </div>
            </div>
          </Alert>
          <div className="text-center py-3">
            <IconCreditCard size={64} className="text-muted mb-3" />
            <h5>Cadastre seu perfil de faturamento</h5>
            <p className="text-muted mb-0">
              Adicione um cartão de crédito ou outro meio de pagamento para continuar.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleGoToBilling}>
            <IconCreditCard size={18} className="me-2" />
            Ir para Faturamento
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center">
          <IconRocket size={28} className="text-primary me-2" />
          Contratar Plano
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {(isTrialActive || isTrialExpired) && (
          <Alert variant={isTrialExpired ? "warning" : "info"} className="mb-4">
            <IconInfoCircle size={18} className="me-2" />
            {isTrialExpired
              ? "Seu período de teste expirou. Contrate agora para continuar utilizando a plataforma."
              : `Você está em período de teste${
                  typeof currentPlan?.days_remaining === "number"
                    ? ` (${currentPlan.days_remaining} dias restantes)`
                    : ""
                }. Contrate agora para garantir o acesso ininterrupto.`}
          </Alert>
        )}

        <div className="d-flex align-items-center justify-content-center mb-4 p-4 bg-light rounded">
          <div className="text-center">
            <Badge bg="primary" className="px-3 py-2 fs-6">
              {plan.name}
            </Badge>
            {plan.monthly_value > 0 && (
              <div className="mt-2 small text-muted">
                {PlanServices.formatCurrency(plan.monthly_value)}/mês
              </div>
            )}
          </div>
        </div>

        <div className="bg-light rounded p-4 mb-4">
          <h6 className="mb-3 d-flex align-items-center">
            <IconCreditCard size={18} className="me-2" />
            Resumo da Contratação
          </h6>
          <div className="row g-3">
            <div className="col-6">
              <small className="text-muted d-block">Plano</small>
              <strong>{plan.name}</strong>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Cobrança hoje</small>
              <strong className="fs-5 text-primary">
                {PlanServices.formatCurrency(plan.monthly_value)}
              </strong>
            </div>
            <div className="col-12 border-top pt-3 mt-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Recorrência:</span>
                <strong>Mensal</strong>
              </div>
            </div>
          </div>
        </div>

        {subscribeError && (
          <Alert variant="danger" className="mb-4">
            <IconAlertTriangle size={18} className="me-2" />
            {subscribeError}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="accept-terms-subscribe"
            label={
              <span>
                Li e aceito os{" "}
                <span
                  style={{
                    color: "var(--primary, #3b82f6)",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontWeight: 500,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowTermsModal(true);
                  }}
                >
                  termos de uso
                </span>
              </span>
            }
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
          />
        </Form.Group>

        <ModalComponent
          props={{
            title: "Termos de Uso",
            content: <NewTermsOfUse />,
            showModal: showTermsModal,
            setShowModal: setShowTermsModal,
            size: "xl",
          }}
        />
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!acceptedTerms || loading}
          className="d-flex align-items-center"
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Processando...
            </>
          ) : (
            <>
              <IconRocket size={18} className="me-2" />
              Confirmar Contratação
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubscribePlanModal;
